import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { getSessionToken, getStoredLoginIdentifier } from '../auth/sessionToken';
import { useAuth } from '../auth/AuthContext';
import { useHeaderDisplayName } from '../hooks/useHeaderDisplayName';
import { useProfileAvatarSrc } from '../hooks/useProfileAvatarSrc';
import { useSocialPosts } from '../hooks/useSocialPosts';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { extractSessionOwnerIdFromAccessToken } from '../utils/assetOwnerMatch';
import { resolveSocialStripUserLabel } from '../utils/headerDisplayName';
import { normalizeStoryUserId } from '../api/socialStoriesApi';
import { resolveStoryGroupAvatarSrc, resolveStoryUserAvatarSrc } from '../utils/profileAvatarDisplay';
import type { SocialPostUserGroup } from '../utils/socialPostsStorage';
import { HorizontalCarousel } from './HorizontalCarousel';
import { ImageSourcePicker, type ImageSourcePickerHandle } from './ImageSourcePicker';
import { ProfileAvatarImage } from './ProfileAvatarImage';
import { SocialPostComposer } from './SocialPostComposer';
import { SocialPostViewer } from './SocialPostViewer';
import styles from './SocialStripCarousel.module.css';

export function SocialStripCarousel() {
  const { t, language } = useI18n();
  const { isLoggedIn } = useAuth();
  const { openAuth } = useAppNavigation();
  const headerDisplayName = useHeaderDisplayName();
  const userAvatarSrc = useProfileAvatarSrc();
  const { groups } = useSocialPosts();
  const [composerImage, setComposerImage] = useState<File | null>(null);
  const [composerCaption, setComposerCaption] = useState('');
  const [viewingUserId, setViewingUserId] = useState<string | null>(null);
  const ignoreStoryPopRef = useRef(false);
  const storyImagePickerRef = useRef<ImageSourcePickerHandle>(null);

  const profileFallback = t('social.yourProfile');
  const guestLabel = t('header.guest');
  const resolvedName =
    isLoggedIn && headerDisplayName !== guestLabel ? headerDisplayName : null;
  const userLabel = resolveSocialStripUserLabel(resolvedName, profileFallback);
  const isHebrew = language === 'he';

  const currentUserId =
    extractSessionOwnerIdFromAccessToken(getSessionToken()) ??
    getStoredLoginIdentifier()?.trim() ??
    'local-user';
  const selfGroup = groups.find(
    (group) => normalizeStoryUserId(group.userId) === normalizeStoryUserId(currentUserId),
  );
  const otherGroups = groups.filter(
    (group) => normalizeStoryUserId(group.userId) !== normalizeStoryUserId(currentUserId),
  );

  const storyPlaylist = useMemo(() => {
    const withPosts = groups.filter((group) => group.posts.length > 0);
    const self = withPosts.find(
      (group) => normalizeStoryUserId(group.userId) === normalizeStoryUserId(currentUserId),
    );
    const others = withPosts.filter(
      (group) => normalizeStoryUserId(group.userId) !== normalizeStoryUserId(currentUserId),
    );
    if (isHebrew) {
      return self ? [self, ...others] : others;
    }
    return self ? [...others, self] : others;
  }, [groups, currentUserId, isHebrew]);

  const contentKey = useMemo(
    () =>
      `${language}-${userLabel}-${userAvatarSrc}-${groups.map((g) => `${g.userId}:${g.posts.length}`).join('|')}`,
    [groups, language, userAvatarSrc, userLabel],
  );

  const onOpenComposer = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (!isLoggedIn) {
        openAuth('login');
        return;
      }
      // Must run in the same user gesture so the OS image picker opens (esp. Android).
      storyImagePickerRef.current?.open();
    },
    [isLoggedIn, openAuth],
  );

  const onStoryImagePicked = useCallback((result: { file: File; caption: string }) => {
    setComposerCaption(result.caption);
    setComposerImage(result.file);
  }, []);

  const closeComposer = useCallback(() => {
    setComposerImage(null);
    setComposerCaption('');
  }, []);

  const openGroupViewer = useCallback((group: SocialPostUserGroup) => {
    if (group.posts.length > 0) {
      setViewingUserId(group.userId);
    }
  }, []);

  const closeViewer = useCallback(() => {
    setViewingUserId(null);
    if (window.history.state?.ofrontendSocialStories) {
      ignoreStoryPopRef.current = true;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    if (!viewingUserId) {
      return;
    }
    window.history.pushState({ ofrontendSocialStories: true }, '');

    const onPopState = () => {
      if (ignoreStoryPopRef.current) {
        ignoreStoryPopRef.current = false;
        return;
      }
      setViewingUserId(null);
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [viewingUserId]);

  const onOpenSelfViewer = useCallback(() => {
    if (selfGroup && selfGroup.posts.length > 0) {
      openGroupViewer(selfGroup);
    }
  }, [selfGroup, openGroupViewer]);

  const renderUserChip = (group: SocialPostUserGroup | null, options: { isSelf: boolean }) => {
    const hasPosts = (group?.posts.length ?? 0) > 0;
    const avatarSrc = resolveStoryUserAvatarSrc(
      options.isSelf ? currentUserId : group?.userId ?? '',
      options.isSelf ? null : resolveStoryGroupAvatarSrc(group),
      currentUserId,
      userAvatarSrc,
      options.isSelf ? undefined : group?.posts,
      options.isSelf ? userLabel : group?.userLabel ?? '',
    );
    const label = options.isSelf ? userLabel : group?.userLabel ?? '';
    const ringClass = hasPosts ? styles.avatarRing : styles.avatarRingMuted;

    return (
      <div key={options.isSelf ? 'self' : group?.userId} className={styles.item}>
        <button
          type="button"
          className={styles.avatarButton}
          onClick={() => {
            if (options.isSelf) {
              onOpenSelfViewer();
              return;
            }
            if (group) {
              openGroupViewer(group);
            }
          }}
          disabled={!hasPosts && !options.isSelf}
          title={
            hasPosts
              ? t('social.viewUserStories', { name: label })
              : options.isSelf
                ? t('social.addPost')
                : label
          }
        >
          <span className={styles.avatarShell}>
            <span className={ringClass}>
              <span className={styles.avatarWrap}>
                <ProfileAvatarImage src={avatarSrc} className={styles.avatar} />
              </span>
            </span>
            {options.isSelf ? (
              <span
                className={styles.addBadge}
                role="button"
                tabIndex={0}
                aria-label={t('social.addPost')}
                onClick={onOpenComposer}
                onKeyDown={(event: KeyboardEvent<HTMLSpanElement>) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpenComposer(event as unknown as MouseEvent);
                  }
                }}
              >
                +
              </span>
            ) : null}
          </span>
        </button>
        <span className={styles.label}>{label}</span>
      </div>
    );
  };

  const userChip = renderUserChip(selfGroup ?? null, { isSelf: true });
  const storyChips = otherGroups.map((group) => renderUserChip(group, { isSelf: false }));

  return (
    <>
      <section className={styles.socialSection} aria-label={t('social.featuredProfiles')}>
        <HorizontalCarousel
          ariaLabel={t('social.featuredProfiles')}
          prevAriaLabel={t('social.prevProfiles')}
          nextAriaLabel={t('social.nextProfiles')}
          scrollClassName={[styles.socialScroll, isHebrew ? styles.scrollRtl : styles.scrollLtr]
            .filter(Boolean)
            .join(' ')}
          initialScrollAlign={isHebrew ? 'start' : 'end'}
          contentKey={contentKey}
        >
          {isHebrew ? [userChip, ...storyChips] : [...storyChips, userChip]}
        </HorizontalCarousel>
      </section>

      <ImageSourcePicker
        ref={storyImagePickerRef}
        allowCaption
        onPickComplete={onStoryImagePicked}
      />

      {composerImage ? (
        <SocialPostComposer
          imageFile={composerImage}
          initialCaption={composerCaption}
          onClose={closeComposer}
        />
      ) : null}
      {viewingUserId && storyPlaylist.length > 0 ? (
        <SocialPostViewer
          playlist={storyPlaylist}
          startUserId={viewingUserId}
          currentUserId={currentUserId}
          currentUserAvatarSrc={userAvatarSrc}
          loop
          onClose={closeViewer}
        />
      ) : null}
    </>
  );
}
